import { useToast } from '@chakra-ui/react'
import { useTranslation } from 'next-i18next/pages'
import {
  ChainWasmErrorCodes,
  ErrorContextCode,
  Exception,
  TransactionChainErrorModule,
} from '@injectivelabs/exceptions'
import posthog from 'posthog-js'
import { SessionException } from '@/context/SessionProvider'

type Props = { key?: SystemMessages; custom?: { title: string } }

export const useAppToast = () => {
  const toast = useToast()
  const { t } = useTranslation()

  const onSessionException = (
    exception: SessionException,
    context?: {
      operationName?: string
      defaultMessage?: string
    }
  ) => {
    if (!(exception instanceof SessionException)) {
      posthog.captureException(exception)
      toast({
        status: 'error',
        title: context?.defaultMessage || 'Something went wrong. Please try again.',
      })
      return
    }

    if (!exception.shouldProceed) return

    if (exception.code == 'USER_REJECTED') {
      console.log('User rejected the request', exception)
      return
    }

    posthog.captureException(exception)

    if (exception.code == 'NOT_SAME_WALLET') {
      toast({
        status: 'error',
        title: 'Please switch to the correct wallet.',
      })
      return
    }

    if (exception.code == 'TOKEN_EXPIRED') {
      toast({
        status: 'error',
        title: 'Session expired. Please relogin to continue.',
      })
      return
    }

    toast({
      status: 'error',
      title: context?.defaultMessage || 'Something went wrong. Please try again.',
    })
  }
  return {
    onSessionException: onSessionException,

    onTransactionError: (exception: Exception) => {

      if (exception instanceof SessionException) {
        return onSessionException(exception)
      }

      const { contextCode, contextModule } = exception

      if (exception.message.includes('request rejected') || exception.message.includes('User rejected the request')) {
        return
      }

      posthog.captureException(exception)

      let message =
        contextCode && contextModule
          ? mappedContractCodes[contextModule as TransactionChainErrorModule]?.[contextCode]
          : null

      const exceptionString = exception.originalMessage?.split(':')?.at(-2)?.replace(' ', '')

      message ??=
        mappedCustomContractMessages[exceptionString as ContractsErrorMessages] || 'TransactionsMessages.error'

      if (exception.message == 'Response closed without headers') {
        exception.message = 'Server is not responding. Please try again later.'
      }

      toast({
        status: 'error',
        title: message ? t(message, 'TransactionsMessages.error') : exception.message,
      })
    },
    onError: (options?: Props) => {
      const message = getMappedMessage(options?.key)

      toast({
        status: 'error',
        title: message
          ? t(message, 'TransactionsMessages.error')
          : options?.custom?.title || t('TransactionsMessages.error'),
      })
    },
    onSuccess: (options?: Props) => {
      const message = getMappedMessage(options?.key)

      toast({
        status: 'success',
        title: message
          ? t(message, 'TransactionsMessages.success')
          : options?.custom?.title || t('TransactionsMessages.success'),
      })
    },
  }
}

const mappedContractCodes: Record<TransactionChainErrorModule, Record<ErrorContextCode, string>> = {
  [TransactionChainErrorModule.Wasmx]: {
    [ChainWasmErrorCodes.ErrEmpty]: 'TransactionsMessages.error',
  },
} as any

enum ContractsErrorMessages {
  AmountTooMuch = 'AmountTooMuch',
  InvalidGameState = 'InvalidGameState',
}

const mappedCustomContractMessages: Record<ContractsErrorMessages, string> = {
  [ContractsErrorMessages.AmountTooMuch]: 'ContractMessages.AmountTooMuch',
  [ContractsErrorMessages.InvalidGameState]: 'ContractMessages.ContractMessages',
}

export enum ApiMessages {
  UserBlocked = 'user-blocked',
  MessageDeleted = 'message-deleted',
}

const api: Record<ApiMessages, string> = {
  [ApiMessages.UserBlocked]: 'TransactionsMessages.user-blocked',
  [ApiMessages.MessageDeleted]: 'TransactionsMessages.message-deleted',
}

export enum AppMessages {
  TextCopied = 'text-copied',
}

type SystemMessages = AppMessages | ApiMessages

const app: Record<AppMessages, string> = {
  [AppMessages.TextCopied]: 'common.text-copied',
}

const mappedMessages = {
  ...api,
  ...app,
}

const getMappedMessage = (key?: SystemMessages) => {
  if (!key) return null
  const mappedMessage = mappedMessages[key]
  if (!mappedMessage) return null
  return mappedMessage
}

export const mapContractError = (error: string | undefined) => {
  return `TransactionsMessages.${error}`
}
